import React, { useEffect, useState, Fragment } from 'react';
import { Observer } from 'mobx-react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Typography,
  Button,
  Menu,
  MenuItem,
  IconButton,
} from '@material-ui/core';
import { format } from 'date-fns';
import { css } from '@emotion/core';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import CirclePlusIcon from '@material-ui/icons/AddCircleOutline';
import PublishIcon from '@material-ui/icons/Publish';
import FilterIcon from '@material-ui/icons/FilterList';
import { toJS } from 'mobx';
import Checkmark from '@material-ui/icons/Check';
import OpenInNew from '@material-ui/icons/OpenInNew';
import { useRootStore } from '../../../stores/BridgeProvider';
import Spinner from '../../util/Spinner';
import { FlexDiv, WideBox } from '../../styles/styles';
import { dateSort } from '../../util/TableUtils';
import { CustomTable, FilterPlaceholder } from '../../common/CustomTable';
import { DealMappedStatus, STATUS_COLOR } from '../dealUtil';
import { NumberHandler } from '../../util/NumberHandler';
import DealsJoyride from '../../joyrides/DealsJoyride';
import { safeEval } from '../../util/UtilFunctions';
import {
  atRiskFilter,
  totalDealsFilter,
  unmappedFilter,
  completeFilter,
} from './advancedFilters';

const checkStyle = css`
  font-size: 1.2rem !important;
  margin-top: -5px !important;
  margin-left: 2px !important;
`;

const StatusCell = (info, statusFunction = DealMappedStatus) => {
  const statusName = statusFunction(info);
  const styleObj = {
    background: STATUS_COLOR[statusName],
    color: 'white',
    opacity: '.9',
    borderRadius: '1rem',
    padding: '5px 10px',
  };
  return <span style={styleObj}>{statusName}</span>;
};

const Deals = () => {
  const { authStore } = useRootStore();
  const history = useHistory();
  const { state } = useLocation();
  const [isLoading, setLoading] = useState(true);
  const [deals, setDeals] = useState(null);
  const [advancedFilters, setadvancedFilters] = useState(
    safeEval(() => state.advancedFilters, []) || [],
  );
  const defaultFiltered = safeEval(() => state.filters, []) || [];

  const getDeals = async () => {
    try {
      const { data } = await authStore.request.get(
        `${process.env.REACT_APP_API}/api/deals`,
      );
      console.log(data);
      setDeals(data);
    } catch (e) {
      console.error(e);
    }
  };

  const fullMappedStatus = props => {
    const {
      APAmount,
      APAmountMapped,
      APAmountPaid,
      ARAmount,
      ARAmountMapped,
      ARAmountPaid,
    } = props;
    const apStatus = DealMappedStatus({
      amount: APAmount,
      mapped: APAmountMapped,
      paid: APAmountPaid,
    });

    const arStatus = DealMappedStatus({
      amount: ARAmount,
      mapped: ARAmountMapped,
      paid: ARAmountPaid,
    });
    const vals = [arStatus, apStatus];

    if (vals.some(val => val === 'Unmapped' || val === 'N/A'))
      return 'Unmapped';

    if (vals.some(val => val === 'Unpaid')) return 'Unpaid';

    // if (vals.every(val => val === 'Complete')) return 'Complete';

    return 'Complete';
  };

  const addOrRemoveFilter = filter => {
    const newList = [...advancedFilters];
    const filterIndex = advancedFilters.findIndex(i => i === filter);
    if (filterIndex > -1) {
      newList.splice(filterIndex, 1);
    } else {
      newList.push(filter);
    }
    setadvancedFilters(newList);
  };

  const customFilterOptions = {
    atRisk: atRiskFilter,
    totalDeals: totalDealsFilter,
    unMapped: unmappedFilter,
    complete: completeFilter,
  };

  const dealColumnsConfig = [
    {
      Header: 'Title',
      accessor: 'Title',
      id: 'title',
      Filter: FilterPlaceholder,
      width: 110,
    },
    {
      Header: 'Number',
      accessor: 'DealNumber',
      id: 'dealNumber',
    },
    {
      Header: 'Created Date',
      accessor: d => format(d.CreatedTimestamp, 'MM/DD/YYYY'),
      id: 'createdDate',
      sortType: dateSort,
      width: 80,
    },
    {
      Header: 'Contract Date',
      accessor: d => format(d.ContractDate, 'MM/DD/YYYY'),
      id: 'contractDate',
      sortType: dateSort,
      width: 80,
    },
    {
      Header: 'Agent',
      accessor: d => d.AgentContactName || 'N/A',
      id: 'agentName',
      width: 110,
    },
    {
      Header: 'AR Amount',
      accessor: d => d.ARAmount || null,
      Cell: ({ row }) =>
        row.original.ARAmount ? (
          <NumberHandler
            num={row.original.ARAmount}
            float={2}
            justify="flex-end"
            currency="$"
          />
        ) : (
          'N/A'
        ),
      id: 'arAmount',
      width: 70,
    },
    // {
    //   Header: 'AR Mapped Status',
    //   accessor: ({ ARAmount, ARAmountMapped, ARAmountPaid }) =>
    //     DealMappedStatus({
    //       amount: ARAmount,
    //       mapped: ARAmountMapped,
    //       paid: ARAmountPaid,
    //     }),
    //   Cell: ({
    //     row: {
    //       original: { ARAmount, ARAmountMapped, ARAmountPaid },
    //     },
    //   }) =>
    //     StatusCell({
    //       amount: ARAmount,
    //       mapped: ARAmountMapped,
    //       paid: ARAmountPaid,
    //     }),
    //   id: 'arMappedStatus',
    // },
    {
      Header: 'AP Amount',
      accessor: d => d.APAmount || null,
      Cell: ({ row }) =>
        row.original.APAmount ? (
          <NumberHandler
            num={row.original.APAmount}
            float={2}
            justify="flex-end"
            currency="$"
          />
        ) : (
          'N/A'
        ),
      id: 8,
      width: 70,
    },
    {
      Header: 'Net Income',
      accessor: d => d.NetIncome || null,
      Cell: ({ row }) =>
        row.original.NetIncome ? (
          <NumberHandler
            num={row.original.NetIncome}
            float={2}
            justify="flex-end"
            currency="$"
          />
        ) : (
          'N/A'
        ),
      id: 'netIncome',
      width: 70,
    },
    // {
    //   Header: 'AP Mapped Status',
    //   accessor: ({ APAmount, APAmountMapped, APAmountPaid }) =>
    //     DealMappedStatus({
    //       amount: APAmount,
    //       mapped: APAmountMapped,
    //       paid: APAmountPaid,
    //     }),
    //   Cell: ({
    //     row: {
    //       original: { APAmount, APAmountMapped, APAmountPaid },
    //     },
    //   }) =>
    //     StatusCell({
    //       amount: APAmount,
    //       mapped: APAmountMapped,
    //       paid: APAmountPaid,
    //     }),
    //   id: 9,
    // },
    {
      Header: 'Mapped Status',
      accessor: val => fullMappedStatus(val),
      Cell: ({ row }) => StatusCell(row.original, fullMappedStatus),
      id: 'mappedStatus',
    },
    {
      Header: 'Status',
      accessor: 'StatusName',
      id: 4,
      width: 80,
    },
    {
      width: 40,
      Cell: ({ row }) => (
        <a
          onClick={e => e.stopPropagation()}
          href={`/deal/${row.original.DealId}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            width: '100%',
            height: '100%',
            color: '#666',
          }}
        >
          <OpenInNew />
        </a>
      ),
      id: 'newTab',
    },
  ];

  // CreatedTimestamp
  // DealNumbers

  useEffect(() => {
    const initialActions = async () => {
      await getDeals();
      setLoading(false);
    };
    initialActions();
  }, []);
  if (isLoading) return <Spinner />;

  const filteredDeals = advancedFilters.reduce((dealList, filter) => {
    try {
      const filterFunction = customFilterOptions[filter];
      return filterFunction(dealList);
    } catch (e) {
      return dealList;
    }
  }, deals);

  const isSelectedCheckmark = fName =>
    advancedFilters.includes(fName) ? <Checkmark css={checkStyle} /> : null;

  return (
    <Observer
      render={() => {
        if (isLoading) return <Spinner />;
        return (
          <FlexDiv>
            <WideBox
              style={{ maxWidth: '1500px', width: '98%' }}
              className="dealsStep1"
            >
              <CustomTable
                data={filteredDeals}
                columns={dealColumnsConfig}
                minRows={1}
                trClassName="react-table-hover-row dealsStep2 dealsStep3"
                getTrProps={({ row }) => ({
                  onClick: () => history.push(`/deal/${row.original.DealId}`),
                })}
                filtersOn={defaultFiltered.length > 0}
                defaultFiltered={defaultFiltered}
                tableHeader={<Typography variant="h2">Deals</Typography>}
                actionables={
                  <Fragment>
                    <PopupState variant="popover" popupId="demo-popup-menu">
                      {popupState => (
                        <React.Fragment>
                          <Button
                            style={{ marginRight: '10px' }}
                            variant="outlined"
                            color={
                              advancedFilters.length > 0 ? 'primary' : 'default'
                            }
                            {...bindTrigger(popupState)}
                          >
                            <FilterIcon
                              style={{
                                marginRight: '4px',
                                marginLeft: '-3px',
                              }}
                            />
                            Filters
                          </Button>
                          <Menu {...bindMenu(popupState)}>
                            <MenuItem
                              onClick={() => addOrRemoveFilter('atRisk')}
                            >
                              At Risk Deals
                              {isSelectedCheckmark('atRisk')}
                            </MenuItem>
                            <MenuItem
                              onClick={() => addOrRemoveFilter('unMapped')}
                            >
                              Unmapped Deals
                              {isSelectedCheckmark('unMapped')}
                            </MenuItem>
                            <MenuItem
                              onClick={() => addOrRemoveFilter('complete')}
                            >
                              Complete Deals
                              {isSelectedCheckmark('complete')}
                            </MenuItem>
                            <MenuItem
                              onClick={() => addOrRemoveFilter('totalDeals')}
                            >
                              Total Deals
                              {isSelectedCheckmark('totalDeals')}
                            </MenuItem>
                          </Menu>
                        </React.Fragment>
                      )}
                    </PopupState>
                    <Button
                      style={{ marginRight: '10px' }}
                      variant="outlined"
                      color="default"
                      component={Link}
                      to="/import"
                    >
                      <PublishIcon
                        style={{
                          marginRight: '4px',
                          marginLeft: '-3px',
                        }}
                      />
                      Import Deals
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      component={Link}
                      to="/deals/new"
                    >
                      <CirclePlusIcon
                        style={{
                          marginRight: '4px',
                          marginLeft: '-3px',
                        }}
                      />
                      New Deal
                    </Button>
                  </Fragment>
                }
                containerStyle={{ padding: '0 2rem' }}
              />
            </WideBox>
            <DealsJoyride />
          </FlexDiv>
        );
      }}
    />
  );
};

export default Deals;
